/**
 * Open-license track driver. Uses HTMLAudioElement when a file is present;
 * falls back to silent elapsed clock so acts still advance in dev.
 */
export class MusicDirector {
  private audio: HTMLAudioElement | null = null;
  private fallbackElapsed = 0;
  private usingFallback = true;
  /** Target run length when silent / loop section estimate */
  readonly fallbackDurationSec: number;

  constructor(fallbackDurationSec = 390) {
    this.fallbackDurationSec = fallbackDurationSec;
  }

  async load(url: string | null): Promise<void> {
    if (!url) {
      this.usingFallback = true;
      return;
    }
    try {
      const a = new Audio(url);
      a.preload = "auto";
      a.loop = false;
      await new Promise<void>((resolve, reject) => {
        a.addEventListener("canplaythrough", () => resolve(), { once: true });
        a.addEventListener("error", () => reject(new Error("audio load failed")), {
          once: true,
        });
        a.load();
      });
      this.audio = a;
      this.usingFallback = false;
    } catch {
      this.audio = null;
      this.usingFallback = true;
    }
  }

  async play(): Promise<void> {
    this.fallbackElapsed = 0;
    if (this.audio) {
      this.audio.currentTime = 0;
      try {
        await this.audio.play();
        this.usingFallback = false;
      } catch {
        this.usingFallback = true;
      }
    } else {
      this.usingFallback = true;
    }
  }

  stop(): void {
    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
    }
    this.fallbackElapsed = 0;
  }

  tick(dt: number): void {
    if (this.usingFallback || !this.audio) {
      this.fallbackElapsed += dt;
    }
  }

  /** 0..1 through the ritual */
  get progress(): number {
    if (!this.usingFallback && this.audio && this.audio.duration > 0) {
      return Math.min(1, this.audio.currentTime / this.audio.duration);
    }
    return Math.min(1, this.fallbackElapsed / this.fallbackDurationSec);
  }

  get intensity(): number {
    const p = this.progress;
    if (p < 0.2) return 0.3;
    if (p < 0.45) return 0.55;
    if (p < 0.7) return 0.8;
    return 1;
  }
}
